import { LANGUAGES, LANGUAGES_MAP } from "@constants/misc";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { Language, LanguageItem } from "@types";
import { getValidLanguage } from "@utils/locales";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useLanguages = (): {
  languages: LanguageItem[];
  currentLanguage: LanguageItem;
  changeLanguage: (language: Language) => void;
  isMultiLanguage: boolean;
} => {
  const { locales = [] } = useMemberSettings();
  const { i18n } = useTranslation();

  const changeLanguage = useCallback(
    (language: Language) => {
      return i18n.changeLanguage(language);
    },
    [i18n],
  );

  const languages = useMemo(() => {
    return locales?.length
      ? locales?.map((locale) => {
          return {
            ...locale,
            // TODO: Use locale.displayName later when backend supports it
            displayName: LANGUAGES_MAP[locale.code as Language]?.displayName,
          };
        })
      : LANGUAGES;
  }, [locales]);

  const currentLanguage = useMemo(() => {
    const currentLangCode = getValidLanguage(languages, i18n.language as Language, LANGUAGES[0].code as Language);
    return languages.find((l) => l.code === currentLangCode) ?? LANGUAGES[0];
  }, [i18n.language, languages]);

  const languageItems = useMemo(() => {
    return languages.map((lang) => ({
      code: lang.code as Language,
      name: lang.displayName,
      flagURL: lang.flagImage?.url || LANGUAGES_MAP[lang.code as Language]?.flagImage?.url || "",
    }));
  }, [languages]);

  const currentLanguageItem = useMemo(() => {
    return {
      code: currentLanguage.code as Language,
      name: currentLanguage.displayName,
      flagURL: currentLanguage.flagImage?.url || LANGUAGES_MAP[currentLanguage.code as Language]?.flagImage?.url || "",
    };
  }, [currentLanguage]);

  return {
    languages: languageItems,
    currentLanguage: currentLanguageItem,
    changeLanguage,
    isMultiLanguage: languages.length > 1,
  };
};
